/**
 * DenverSalesforceLandingPage_v01.tsx
 * ---------------------------------------------------------------
 * A responsive landing page for denversalesforce.com built with
 * React, TypeScript, and react-bootstrap.
 *
 * Version: v01
 * Date: 2025-08-23
 * ---------------------------------------------------------------
 * Sections:
 * - Top Navbar
 * - Hero with CTA
 * - Trust / Badges
 * - Services (Cards)
 * - Why Us (Bulleted value props)
 * - Customer Logos (placeholder)
 * - Contact / Lead Form
 * - FAQ (Accordion)
 * - Footer with quick links + contact
 */

import React, { useMemo, useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
} from "react-bootstrap";

type LeadPayload = {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  role?: string;
  interest: "Implementation" | "Consulting" | "Admin Support" | "Integration" | "Audit/Health Check" | "Training";
  message?: string;
  utmSource?: string;
  utmCampaign?: string;
};

const VERSION = "v01";

const navLinks = [
  { href: "#services", label: "Services" },
  { href: "#why-us", label: "Why Us" },
  { href: "#contact", label: "Contact" },
  { href: "#faq", label: "FAQ" },
];

const serviceCards = [
  {
    title: "Implementation & Quick Starts",
    text: "Launch Salesforce quickly and correctly—Sales, Service, or Experience Cloud setups tailored to your Denver market.",
    bullets: ["Discovery & Design", "Data Model & Security", "Automation & Reports", "Enablement"],
    icon: "🚀",
  },
  {
    title: "Admin-as-a-Service",
    text: "On-demand certified admins to keep your org humming—backlog, automations, fields, flows, and user support.",
    bullets: ["Backlog Triage", "Flow/Automation", "Dashboards", "User Management"],
    icon: "🛠️",
  },
  {
    title: "Integrations",
    text: "Connect Salesforce with the tools you use—accounting, marketing automation, telephony, data lakes, and more.",
    bullets: ["Mulesoft / APIs", "Zapier / Workato", "Data Pipelines", "Telephony (CTI)"],
    icon: "🔌",
  },
  {
    title: "Health Check & Optimization",
    text: "We audit your org, untangle tech debt, and improve performance, adoption, and reporting trust.",
    bullets: ["Security Review", "Field/Flow Hygiene", "Performance Tuning", "Adoption Plan"],
    icon: "🩺",
  },
  {
    title: "RevOps Dashboards",
    text: "Board-ready dashboards for pipeline, forecast, and cohort analytics. Your revenue truth—at a glance.",
    bullets: ["Forecast Accuracy", "Win/Loss", "Funnel KPIs", "Cohorts"],
    icon: "📊",
  },
  {
    title: "Team Training",
    text: "Role-based training for admins, sellers, and service. Practical, Denver-centric exercises and templates.",
    bullets: ["Admin 101/201", "AE/CSR Playbooks", "Report Building", "Best Practices"],
    icon: "🎓",
  },
];

const faqs = [
  {
    q: "How fast can you start?",
    a: "Most small engagements can start within 5 business days. For larger programs, we align a kickoff within 1–2 weeks with a clear roadmap.",
  },
  {
    q: "Do you work with existing partners or internal teams?",
    a: "Yes. We embed with your stakeholders and current vendors, coordinating sprints, governance, and documentation.",
  },
  {
    q: "What industries do you support?",
    a: "Commonly B2B SaaS, professional services, manufacturing, and financial services—plus Denver-local SMBs.",
  },
  {
    q: "Do you offer fixed-fee packages?",
    a: "We offer scoped quick-starts and health checks at fixed fees, and flexible monthly blocks for ongoing admin needs.",
  },
];

const DenverSalesforceLandingPage_v01: React.FC = () => {
  // Simple UTM capture (replace with real router/query parsing as needed)
  const utm = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get("utm_source") ?? undefined,
      utmCampaign: params.get("utm_campaign") ?? undefined,
    };
  }, []);

  const [submitting, setSubmitting] = useState(false);
  const [submittedOk, setSubmittedOk] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [lead, setLead] = useState<LeadPayload>({
    name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    interest: "Implementation",
    message: "",
    ...utm,
  });

  const handleChange = <K extends keyof LeadPayload>(key: K, val: LeadPayload[K]) => {
    setLead((prev) => ({ ...prev, [key]: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg(null);

    try {
      // TODO: Replace with your API (API Gateway/Lambda, Formspark, etc.)
      // Example payload
      const payload = {
        ...lead,
        page: "denversalesforce.com",
        dateTimeUTC: new Date().toISOString(),
      };

      // Simulate network
      await new Promise((r) => setTimeout(r, 1000));

      // Example: const res = await fetch("/api/leads", { method: "POST", body: JSON.stringify(payload) });
      // if (!res.ok) throw new Error("Failed to submit");

      console.log("Lead payload →", payload);
      setSubmittedOk(true);
      setLead((prev) => ({ ...prev, message: "" }));
    } catch (err: any) {
      setErrorMsg(err?.message ?? "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* NAVBAR */}
      <Navbar expand="lg" bg="light" className="border-bottom sticky-top" style={{ backdropFilter: "saturate(180%) blur(4px)" }}>
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Denver<span className="text-primary">Salesforce</span>
            <Badge bg="secondary" className="ms-2">v{VERSION}</Badge>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="top-nav" />
          <Navbar.Collapse id="top-nav" className="justify-content-end">
            <Nav className="gap-2">
              {navLinks.map((n) => (
                <Nav.Link key={n.href} href={n.href}>{n.label}</Nav.Link>
              ))}
              <Button href="#contact" variant="primary">Get a Quote</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <header className="py-5 py-md-6 bg-light border-bottom">
        <Container>
          <Row className="align-items-center g-4">
            <Col md={7}>
              <h1 className="display-5 fw-bold">
                Denver-Based <span className="text-primary">Salesforce Experts</span>
              </h1>
              <p className="lead text-muted">
                Implement. Integrate. Optimize. From quick-starts to RevOps dashboards, we help Colorado teams turn Salesforce into a growth engine.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <Button href="#contact" size="lg" variant="primary">Talk to an Expert</Button>
                <Button href="#services" size="lg" variant="outline-primary">Explore Services</Button>
              </div>
              <div className="mt-3 small text-muted">
                Trusted by Denver SMBs and mid-market teams across SaaS, services, manufacturing, and finance.
              </div>
            </Col>
            <Col md={5}>
              <Card className="shadow-sm">
                <Card.Body>
                  <h5 className="mb-3">Quick Project Fit Quiz</h5>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      window.location.hash = "contact";
                    }}
                  >
                    <Form.Group className="mb-2">
                      <Form.Label>What do you need most?</Form.Label>
                      <Form.Select defaultValue="Implementation">
                        <option>Implementation</option>
                        <option>Consulting</option>
                        <option>Admin Support</option>
                        <option>Integration</option>
                        <option>Audit/Health Check</option>
                        <option>Training</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Timeline</Form.Label>
                      <Form.Select defaultValue="2–4 weeks">
                        <option>ASAP (1–2 weeks)</option>
                        <option>2–4 weeks</option>
                        <option>1–2 months</option>
                        <option>Planning/Exploring</option>
                      </Form.Select>
                    </Form.Group>
                    <Button type="submit" variant="primary" className="w-100">
                      See if we’re a fit →
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </header>

      {/* TRUST */}
      <section className="py-4">
        <Container>
          <Row className="text-center">
            <Col>
              <div className="d-flex flex-wrap justify-content-center align-items-center gap-3 text-muted">
                <span>🏔️ Colorado Local</span>
                <span>•</span>
                <span>⭐ 4.9/5 Client Rating</span>
                <span>•</span>
                <span>🛡️ Security & Compliance Minded</span>
                <span>•</span>
                <span>📈 RevOps Focus</span>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-5 bg-white">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold">Services</h2>
              <p className="text-muted m-0">Modular packages that meet you where you are.</p>
            </Col>
          </Row>
          <Row xs={1} md={2} lg={3} className="g-4">
            {serviceCards.map((s) => (
              <Col key={s.title}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    <div className="display-6">{s.icon}</div>
                    <Card.Title className="mt-2">{s.title}</Card.Title>
                    <Card.Text className="text-muted">{s.text}</Card.Text>
                    <ul className="mb-0">
                      {s.bullets.map((b) => (
                        <li key={b} className="text-muted">{b}</li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* WHY US */}
      <section id="why-us" className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold">Why DenverSalesforce?</h2>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>Local + Senior Talent</Card.Title>
                  <Card.Text className="text-muted">
                    You work with senior Salesforce specialists who understand Colorado markets, seasonality, and growth patterns.
                  </Card.Text>
                  <ul className="mb-0">
                    <li>Certified consultants & admins</li>
                    <li>Industry playbooks & templates</li>
                    <li>Clear documentation & handoff</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Body>
                  <Card.Title>Outcome-Focused</Card.Title>
                  <Card.Text className="text-muted">
                    We anchor work to business outcomes—faster deal cycles, happier customers, and reliable reporting.
                  </Card.Text>
                  <ul className="mb-0">
                    <li>RevOps dashboards that leaders love</li>
                    <li>Automation to reduce clicks</li>
                    <li>Governance to keep orgs clean</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CUSTOMER LOGOS (placeholder) */}
      <section className="py-5 bg-white">
        <Container>
          <Row className="mb-3">
            <Col>
              <h3 className="fw-bold">Selected Clients</h3>
              <p className="text-muted m-0">Logos shown for illustration—swap with your client marks.</p>
            </Col>
          </Row>
          <Row className="g-3 align-items-center">
            {Array.from({ length: 6 }).map((_, i) => (
              <Col key={i} xs={6} md={4} lg={2}>
                <div className="border rounded-3 p-3 text-center text-muted">Logo {i + 1}</div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-5 bg-light border-top">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold">Let’s talk about your Salesforce goals</h2>
              <p className="text-muted m-0">
                Tell us a bit about your team and what you’d like to achieve. We’ll reply within 1 business day.
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            <Col md={7}>
              <Card className="shadow-sm">
                <Card.Body>
                  <Form onSubmit={handleSubmit}>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group controlId="name">
                          <Form.Label>Full Name</Form.Label>
                          <Form.Control
                            required
                            value={lead.name}
                            onChange={(e) => handleChange("name", e.target.value)}
                            placeholder="Jane Doe"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="email">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            required
                            value={lead.email}
                            onChange={(e) => handleChange("email", e.target.value)}
                            placeholder="jane@company.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="phone">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            value={lead.phone ?? ""}
                            onChange={(e) => handleChange("phone", e.target.value)}
                            placeholder="(303) 555-0123"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="company">
                          <Form.Label>Company</Form.Label>
                          <Form.Control
                            value={lead.company ?? ""}
                            onChange={(e) => handleChange("company", e.target.value)}
                            placeholder="Acme Co."
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="role">
                          <Form.Label>Role</Form.Label>
                          <Form.Control
                            value={lead.role ?? ""}
                            onChange={(e) => handleChange("role", e.target.value)}
                            placeholder="VP Sales, RevOps, Admin..."
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="interest">
                          <Form.Label>Primary Interest</Form.Label>
                          <Form.Select
                            value={lead.interest}
                            onChange={(e) =>
                              handleChange(
                                "interest",
                                e.target.value as LeadPayload["interest"]
                              )
                            }
                          >
                            <option value="Implementation">Implementation</option>
                            <option value="Consulting">Consulting</option>
                            <option value="Admin Support">Admin Support</option>
                            <option value="Integration">Integration</option>
                            <option value="Audit/Health Check">Audit/Health Check</option>
                            <option value="Training">Training</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group controlId="message">
                          <Form.Label>What’s the goal?</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={lead.message ?? ""}
                            onChange={(e) => handleChange("message", e.target.value)}
                            placeholder="Tell us about your current challenges and desired outcomes."
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} className="d-flex align-items-center gap-2">
                        <Button type="submit" disabled={submitting}>
                          {submitting ? (
                            <>
                              <Spinner size="sm" animation="border" className="me-2" />
                              Sending...
                            </>
                          ) : (
                            "Request Consultation"
                          )}
                        </Button>
                        {submittedOk && <Badge bg="success">Thanks—check your email!</Badge>}
                      </Col>
                    </Row>

                    {errorMsg && (
                      <Alert variant="danger" className="mt-3 mb-0">
                        {errorMsg}
                      </Alert>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>

            <Col md={5}>
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <h5 className="mb-2">Based in Denver, serving the Rockies</h5>
                  <p className="text-muted">
                    We partner with Colorado companies to ship pragmatic Salesforce improvements without the red tape.
                  </p>
                  <ul className="mb-4">
                    <li>Remote-first with on-site options</li>
                    <li>Weekly sprint cadence</li>
                    <li>Clear project reporting</li>
                  </ul>

                  <div className="border rounded-3 p-3">
                    <div className="small text-muted">Contact</div>
                    <div>hello@denversalesforce.com</div>
                    <div>(303) 555-0123</div>
                    <div className="mt-2 small text-muted">Mon–Fri, 9am–5pm MT</div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-5 bg-white border-top">
        <Container>
          <Row className="mb-4">
            <Col><h2 className="fw-bold">FAQ</h2></Col>
          </Row>
          <Row>
            <Col md={10} lg={8}>
              <Accordion alwaysOpen>
                {faqs.map((f, idx) => (
                  <Accordion.Item eventKey={String(idx)} key={f.q}>
                    <Accordion.Header>{f.q}</Accordion.Header>
                    <Accordion.Body className="text-muted">{f.a}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FOOTER */}
      <footer className="py-4 bg-light border-top">
        <Container>
          <Row className="align-items-center g-3">
            <Col md={6}>
              <div className="text-muted">
                © {new Date().getFullYear()} DenverSalesforce. All rights reserved.
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end">
              <div className="d-flex gap-3">
                <a href="#services" className="text-decoration-none">Services</a>
                <a href="#contact" className="text-decoration-none">Contact</a>
                <a href="#faq" className="text-decoration-none">FAQ</a>
              </div>
            </Col>
          </Row>
        </Container>
      </footer>
    </>
  );
};

export default DenverSalesforceLandingPage_v01;
